import { IconArrowUp, IconChevronDown } from "@/assets/icons";
import { CheckboxNew } from "@/modules/irohana-components/elements/CheckboxNew";
import { text } from "@/theme/colors";
import { spacing } from "@/theme/variables";
import { Collapse, Divider, Stack, Typography } from "@mui/material";
import { ChangeEvent, FC, useState } from "react";
import { ALL_EMAIL_DELIVERY_VALUES, NOTIFICATION_GROUPS } from "@/features/user-settings/constants/notification-options";

type TNotificationSettingSectionProps = {
  value: string[];
  onChange?: (next: string[]) => void;
  readOnly?: boolean;
  /** "view" keeps the detail-page layout; "edit" follows the ユーザー設定編集 design (collapsible groups). */
  variant?: "view" | "edit";
};

const CollapsibleHeader: FC<{ title: string; isOpen: boolean; onToggle: () => void }> = ({ title, isOpen, onToggle }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ cursor: "pointer" }} onClick={onToggle}>
    <Typography variant="body14Bold" color={text.primary}>
      {title}
    </Typography>
    {isOpen ? <IconArrowUp /> : <IconChevronDown />}
  </Stack>
);

export const NotificationSettingSection: FC<TNotificationSettingSectionProps> = ({ value, onChange, readOnly = false, variant = "view" }) => {
  const isEdit = variant === "edit";
  const [isAllOpen, setIsAllOpen] = useState(true);
  const [isIndividualOpen, setIsIndividualOpen] = useState(!isEdit);

  const isAllChecked = ALL_EMAIL_DELIVERY_VALUES.every((option) => value.includes(option));

  const handleToggleAll = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    onChange?.(checked ? [...ALL_EMAIL_DELIVERY_VALUES] : []);
  };

  const handleToggleOne = (option: string, checked: boolean) => {
    onChange?.(checked ? [...value, option] : value.filter((item) => item !== option));
  };

  const allCheckbox = <CheckboxNew nonControl label="全てのメール通知" checked={isAllChecked} disabled={readOnly} onChange={handleToggleAll} />;

  const individualOptions = (
    <Stack gap={spacing.md} pt={spacing.sm}>
      {NOTIFICATION_GROUPS.map((group, groupIndex) => (
        <Stack key={group.title} gap={spacing.xs}>
          {groupIndex > 0 && <Divider />}
          <Typography variant="cap12" color={text.secondary}>
            {group.title}
          </Typography>
          <Stack gap={spacing.xs}>
            {group.options.map((option) => (
              <CheckboxNew
                key={option.value}
                nonControl
                label={option.label}
                subLabel={option.subLabel}
                checked={value.includes(option.value)}
                disabled={readOnly}
                onChange={(_, checked) => handleToggleOne(option.value, checked)}
              />
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Stack gap={spacing.sm}>
      {isEdit ? (
        <Stack gap={spacing.sm}>
          <CollapsibleHeader title="全てのメール通知" isOpen={isAllOpen} onToggle={() => setIsAllOpen((prev) => !prev)} />
          <Collapse in={isAllOpen}>{allCheckbox}</Collapse>
        </Stack>
      ) : (
        allCheckbox
      )}

      <Stack>
        <CollapsibleHeader title="個別メール通知" isOpen={isIndividualOpen} onToggle={() => setIsIndividualOpen((prev) => !prev)} />
        <Collapse in={isIndividualOpen}>{individualOptions}</Collapse>
      </Stack>
    </Stack>
  );
};
